/**
 * 기사 검토 (사용자 › /app/articles).
 *
 * 운영자가 보낸 칼럼 원고를 광고주가 읽고, 필요하면 고치고, 사진을 붙여 승인한다.
 * ★ 고친 내용은 운영자 화면에 보낸 원고와 대조돼 표시된다 — 광고주에게는 원고만 보인다.
 * ★ 기사는 사진이 **필수**다 — 대행사가 사진 없는 기사를 받지 않는다. 사진이 없으면 승인 버튼을 잠근다(서버도 막는다).
 * ★ 지난 기사에 올린 사진이 기본으로 들어와 있을 수 있다(보낼 때 서버가 붙인다) — 바꾸려면 지우고 새로 올린다.
 * ★ 의료기관 기사는 올릴 수 있는 사진이 두 종류뿐이다(대행사 기준) — 대표원장 프로필 사진 · 환자에게 설명하는 제스처 사진.
 *   내용은 기계가 가리지 못하므로 안내하고, 올릴 때 한 번 더 확인받는다.
 * ★ 승인하면 광고주도 운영자도 더는 고칠 수 없다.
 */
import { SHELL } from '../lib/branding.ts';
import {
  ARTICLE_PHOTO_MAX_BYTES,
  ARTICLE_PHOTO_MAX_COUNT,
  ARTICLE_PHOTO_MIN_COUNT,
  ARTICLE_PHOTO_TYPES,
  MEDICAL_ARTICLE_PHOTOS,
  type ReviewArticleSummary,
} from '@geo-admin/shared/contract/api/article.ts';
import { article } from '@geo-admin/shared/contract/api/index.ts';
import { Link, useParams } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { type ChangeEvent, useEffect, useState } from 'react';
import { ArticlePreview, PhotoGrid, PublicationList, readBase64 } from '../components/ArticleParts.tsx';
import { DataTable } from '../components/data-table.tsx';
import { PageTitle } from '../components/PageTitle.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { FieldError, Input, Label, Textarea } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

const ACCEPT = ARTICLE_PHOTO_TYPES.join(',');
const MAX_MB = ARTICLE_PHOTO_MAX_BYTES / 1024 / 1024;

const REVIEW_STEPS = [
  { title: '원고 확인', detail: '내용을 읽어 봐 주세요' },
  { title: '사진 (필수) · 수정', detail: `사진을 ${ARTICLE_PHOTO_MIN_COUNT}장 이상 올려 주세요` },
  { title: '승인', detail: '승인 후에는 고칠 수 없습니다' },
];

export function ReviewListPage() {
  const list = useApi(article.listReviewArticles);
  const columns: ColumnDef<ReviewArticleSummary, unknown>[] = [
    {
      header: '제목',
      accessorKey: 'title',
      cell: ({ row }) => (
        <Link to="/app/articles/$id" params={{ id: row.original.id }} className="font-medium hover:underline">
          {row.original.title}
        </Link>
      ),
    },
    // 브랜드가 여럿인 계정은 어느 브랜드의 기사인지가 먼저다
    { header: '브랜드', accessorKey: 'companyName' },
    {
      header: '상태',
      accessorKey: 'status',
      cell: ({ row }) =>
        row.original.status === 'published' ? (
          <Badge tone="blue">게재 완료 {row.original.publicationCount}곳</Badge>
        ) : row.original.status === 'approved' ? (
          <Badge tone="green">승인 완료</Badge>
        ) : (
          <Badge tone="amber">검토 요청</Badge>
        ),
    },
    {
      header: '사진',
      id: 'photos',
      cell: ({ row }) =>
        row.original.status === 'sent' && row.original.photoCount < ARTICLE_PHOTO_MIN_COUNT ? (
          <Badge tone="amber">필수 · 0장</Badge>
        ) : (
          `${row.original.photoCount}장`
        ),
    },
    { header: '받은 날', accessorKey: 'sentAt', cell: ({ row }) => dateTime(row.original.sentAt) },
  ];

  return (
    <>
      <PageTitle
        title="기사 검토"
        description={`${SHELL.serviceName} 가 작성한 칼럼 원고입니다. 내용을 확인하고 필요하면 고친 뒤 승인해 주세요`}
      />
      <Card>
        <CardBody>
          {list.error ? (
            <ErrorBox error={list.error} />
          ) : !list.data ? (
            <Loading />
          ) : (
            <DataTable columns={columns} data={list.data.articles} empty="검토할 기사가 없습니다" />
          )}
        </CardBody>
      </Card>
    </>
  );
}

export function ReviewDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const got = useApi(article.getReviewArticle, { params: { id } });
  const edit = useApiMutation(article.editReviewArticle);
  const approve = useApiMutation(article.approveReviewArticle);
  const upload = useApiMutation(article.uploadReviewPhoto);
  const removePhoto = useApiMutation(article.deleteReviewPhoto);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!got.data) return;
    setTitle(got.data.article.title);
    setBody(got.data.article.body);
  }, [got.data]);

  if (got.error) return <ErrorBox error={got.error} />;
  if (!got.data) return <Loading />;
  const a = got.data.article;
  const open = a.status === 'sent';
  const dirty = title !== a.title || body !== a.body;
  const busy = edit.isPending || approve.isPending || upload.isPending || removePhoto.isPending;
  const missingPhoto = a.photos.length < ARTICLE_PHOTO_MIN_COUNT;
  const defaultCount = a.photos.filter((p) => p.origin === 'default').length;
  const previousCount = a.photos.filter((p) => p.origin === 'previous').length;

  const run = async (job: () => Promise<unknown>, done: string | null) => {
    setError(null);
    setNotice(null);
    try {
      await job();
      if (done) setNotice(done);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const save = () =>
    run(
      () => edit.mutateAsync({ params: { id }, body: { title, subtitle: a.subtitle, body } }),
      '고친 내용을 저장했습니다',
    );

  const onFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    if (a.photos.length + files.length > ARTICLE_PHOTO_MAX_COUNT) {
      return setError(`사진은 ${ARTICLE_PHOTO_MAX_COUNT}장까지 올릴 수 있습니다`);
    }
    for (const f of files) {
      if (!(ARTICLE_PHOTO_TYPES as readonly string[]).includes(f.type))
        return setError(`${f.name}: JPG·PNG·WEBP 사진만 올릴 수 있습니다`);
      if (f.size > ARTICLE_PHOTO_MAX_BYTES) return setError(`${f.name}: 한 장에 ${MAX_MB}MB 까지 올릴 수 있습니다`);
    }
    if (
      files.length > 0 &&
      a.medical &&
      !confirm(
        `올릴 수 있는 사진은 아래 두 가지뿐입니다.\n${MEDICAL_ARTICLE_PHOTOS.map((p) => `· ${p}`).join('\n')}\n\n그 외 사진(시술 전후·건물 외관·장비·로고·홍보 이미지 등)은 언론사에서 받지 않습니다. 이 사진이 맞나요?`,
      )
    )
      return;
    await run(async () => {
      for (const f of files) {
        await upload.mutateAsync({
          params: { id },
          body: {
            fileName: f.name,
            contentType: f.type as (typeof ARTICLE_PHOTO_TYPES)[number],
            dataBase64: await readBase64(f),
          },
        });
      }
    }, `사진 ${files.length}장을 올렸습니다`);
  };

  const onApprove = async () => {
    if (missingPhoto) return setError(`기사는 사진을 ${ARTICLE_PHOTO_MIN_COUNT}장 이상 올려야 승인할 수 있습니다`);
    const photoLine = `사진 ${a.photos.length}장이 함께 발행됩니다.`;
    if (!confirm(`이 기사를 승인합니다.\n${photoLine}\n승인한 뒤에는 고칠 수 없습니다.`)) return;
    await run(async () => {
      // 저장하지 않은 수정이 있으면 먼저 저장한다 — 승인은 저장된 원고로 한다
      if (dirty) await edit.mutateAsync({ params: { id }, body: { title, subtitle: a.subtitle, body } });
      await approve.mutateAsync({ params: { id } });
    }, '승인했습니다. 감사합니다');
  };

  return (
    <>
      <PageTitle
        title="기사 검토"
        description={`받은 날 ${dateTime(a.sentAt)}${a.approvedAt ? ` · 승인 ${dateTime(a.approvedAt)}` : ''}`}
        actions={
          <Link to="/app/articles" className="text-sm text-slate-500 hover:text-slate-900">
            ← 기사 목록
          </Link>
        }
      />
      <Card>
        <CardBody className="grid gap-4 text-sm text-slate-600">
          {open ? (
            <>
              <ol className="grid gap-2 sm:grid-cols-3">
                {REVIEW_STEPS.map((step, i) => (
                  <li key={step.title} className="flex items-start gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                      {i + 1}
                    </span>
                    <span>
                      <span className="block font-semibold text-slate-900">{step.title}</span>
                      <span className="text-xs text-slate-500">{step.detail}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <div className="grid gap-3 md:grid-cols-2">
                <section className="rounded-lg border border-red-200 bg-red-50/60 p-4">
                  <h3 className="mb-2 flex items-center gap-2 font-semibold text-red-900">
                    사진 안내 <Badge tone="red">필수</Badge>
                  </h3>
                  <ul className="grid gap-1.5 leading-relaxed">
                    <li>
                      <b className="text-slate-900">
                        기사는 언론사 규정상 사진이 꼭 있어야 합니다. 사진을 {ARTICLE_PHOTO_MIN_COUNT}장 이상 올려야
                        승인할 수 있습니다.
                      </b>
                    </li>
                    {a.medical ? (
                      <>
                        <li>
                          <span className="font-semibold text-slate-900">
                            의료기관 기사에 올릴 수 있는 사진은 아래 두 가지뿐입니다. 둘 중 하나는 꼭 올려 주세요.
                          </span>
                          <ol className="mt-1 grid gap-0.5 pl-1 font-medium text-slate-900">
                            {MEDICAL_ARTICLE_PHOTOS.map((p, i) => (
                              <li key={p}>
                                {i + 1}. {p}
                              </li>
                            ))}
                          </ol>
                        </li>
                        <li className="text-slate-600">
                          시술 전후·건물 외관·장비·로고·홍보 이미지 등 그 외 사진은 언론사에서 받지 않습니다.
                        </li>
                      </>
                    ) : (
                      <li>대표자 프로필 사진처럼 기사 내용과 어울리는 사진을 올려 주세요.</li>
                    )}
                    <li>사진의 크기와 위치는 언론사에서 알맞게 조정하니 신경 쓰지 않으셔도 됩니다.</li>
                    <li className="text-xs text-slate-500">
                      JPG·PNG·WEBP · 한 장 {MAX_MB}MB · 최대 {ARTICLE_PHOTO_MAX_COUNT}장
                    </li>
                  </ul>
                </section>
                <section className="rounded-lg border border-amber-200 bg-amber-50/70 p-4">
                  <h3 className="mb-2 font-semibold text-amber-900">발행 안내</h3>
                  <p className="leading-relaxed">
                    <b className="text-slate-900">최종 기사는 언론사 편집을 거쳐 발행됩니다.</b> 언론사 편집 기준에 따라
                    제목·문장·문단 구성이 일부 바뀔 수 있어, 실제 게재된 기사는 승인하신 원고와 조금 다를 수 있습니다.
                  </p>
                </section>
              </div>
            </>
          ) : a.status === 'published' ? (
            <p className="flex items-center gap-2">
              <Badge tone="blue">게재 완료</Badge> 언론사에 실린 기사입니다. 아래 링크에서 확인하실 수 있습니다.
            </p>
          ) : (
            <p className="flex items-center gap-2">
              <Badge tone="green">승인 완료</Badge> 승인한 기사는 더 고칠 수 없습니다. 언론사에 실리면 이 화면에 기사
              링크가 표시됩니다.
            </p>
          )}
        </CardBody>
      </Card>

      {a.publications.length > 0 ? (
        <Card>
          <CardHeader
            title={`언론사 게재 ${a.publications.length}곳`}
            description="제목을 누르면 실린 기사가 열립니다"
          />
          <CardBody>
            <PublicationList publications={a.publications} />
          </CardBody>
        </Card>
      ) : null}

      {a.status === 'published' ? null : (
        <Card>
          <CardHeader
            title={`사진 ${a.photos.length}장`}
            description={
              a.photos.length > 0
                ? open && defaultCount + previousCount > 0
                  ? `${defaultCount > 0 ? `등록해 두신 사진 ${defaultCount}장` : `지난 기사에 올리신 사진 ${previousCount}장`}을 미리 넣어 두었습니다. 그대로 쓰셔도 되고, 바꾸시려면 삭제한 뒤 새로 올려 주세요.`
                  : undefined
                : `기사는 사진이 필수입니다 — ${ARTICLE_PHOTO_MIN_COUNT}장 이상 올려 주세요`
            }
            actions={
              open ? (
                <label className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-100">
                  사진 올리기
                  <input
                    type="file"
                    accept={ACCEPT}
                    multiple
                    className="hidden"
                    onChange={(e) => void onFiles(e)}
                    disabled={busy}
                  />
                </label>
              ) : null
            }
          />
          {a.photos.length > 0 ? (
            <CardBody>
              <PhotoGrid
                articleId={a.id}
                photos={a.photos}
                realm="app"
                onDelete={
                  open
                    ? (photoId) =>
                        void run(
                          () => removePhoto.mutateAsync({ params: { id }, query: { photoId } }),
                          '사진을 지웠습니다',
                        )
                    : undefined
                }
              />
            </CardBody>
          ) : null}
        </Card>
      )}

      {/* 게재 완료면 사진·원고·미리보기를 접는다 — 실린 기사는 위 언론사 링크로 본다 */}
      {a.status === 'published' ? null : (
        <div className="grid gap-4 xl:grid-cols-2">
          <Card>
            <CardHeader
              title={open ? '원고 고치기' : '원고'}
              actions={
                open ? (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => void save()} disabled={!dirty || busy}>
                      저장
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void onApprove()}
                      disabled={busy || missingPhoto}
                      title={missingPhoto ? '사진을 1장 이상 올려야 승인할 수 있습니다' : undefined}
                    >
                      승인하기
                    </Button>
                  </div>
                ) : null
              }
            />
            <CardBody className="grid gap-3">
              <Label>
                제목
                <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} disabled={!open} />
              </Label>
              <Label>
                본문
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={22} disabled={!open} />
              </Label>
              {notice ? <p className="text-sm text-emerald-700">{notice}</p> : null}
              <FieldError>{error}</FieldError>
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="미리보기" />
            <CardBody>
              <ArticlePreview title={title} subtitle={a.subtitle} body={body} />
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}
